import DateWeekInput from 'components/storefront/components/DateWeekInput';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';
import { Switch } from 'shadcn/components/ui/Switch';

import { SEGMENT_OPTIONS } from '../utils';

/**
 * Filter controls for the bulk apply dialog.
 * Includes week picker, pack group filter, segment filter, and include products toggle.
 *
 * @param {boolean} disabled - Whether filters are disabled
 * @param {string} filterPackGroup - Current pack group filter value
 * @param {string} filterSegment - Current segment filter value
 * @param {Date|null} filterWeekDate - Currently selected week date
 * @param {boolean} includeProducts - Whether to include products when applying changes
 * @param {(value: boolean) => void} onIncludeProductsChange - Callback when include products changes
 * @param {(value: string) => void} onPackGroupChange - Callback when pack group changes
 * @param {(value: string) => void} onSegmentChange - Callback when segment changes
 * @param {(date: Date) => void} onWeekChange - Callback when week changes
 * @param {Array} packGroups - Available pack groups
 * @param {(open: boolean) => void} setWeekPopoverOpen - Set week popover open state
 * @param {boolean} weekPopoverOpen - Whether week popover is open
 * @returns {JSX.Element}
 */
function BulkApplyFilters({
    disabled,
    filterPackGroup,
    filterSegment,
    filterWeekDate,
    includeProducts,
    onIncludeProductsChange,
    onPackGroupChange,
    onSegmentChange,
    onWeekChange,
    packGroups,
    setWeekPopoverOpen,
    weekPopoverOpen,
}) {
    return (
        <div className="flex flex-wrap items-center gap-3">
            <div>
                <DateWeekInput
                    date={filterWeekDate}
                    onSelectDate={(date) => {
                        onWeekChange(date);
                        setWeekPopoverOpen(false);
                    }}
                    popoverOpen={weekPopoverOpen}
                    setPopoverOpen={setWeekPopoverOpen}
                />
            </div>

            <Select value={filterPackGroup} onValueChange={onPackGroupChange} disabled={disabled}>
                <SelectTrigger>
                    <div className="flex gap-x-2">
                        <span className="font-medium">Pack Group</span>
                        {filterPackGroup === 'all' ? (
                            <span className="text-muted-foreground">All</span>
                        ) : (
                            <SelectValue />
                        )}
                    </div>
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {packGroups.map((pg) => (
                        <SelectItem key={pg.id} value={String(pg.id)}>
                            {pg.name}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select value={filterSegment} onValueChange={onSegmentChange} disabled={disabled}>
                <SelectTrigger>
                    <div className="flex gap-x-2">
                        <span className="font-medium">Segment</span>
                        {filterSegment === 'all' ? <span className="text-muted-foreground">All</span> : <SelectValue />}
                    </div>
                </SelectTrigger>
                <SelectContent>
                    {SEGMENT_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <div className="flex items-center gap-2 ml-auto">
                <Switch
                    id="include-products"
                    checked={includeProducts}
                    onCheckedChange={onIncludeProductsChange}
                    disabled={disabled}
                />
                <Label htmlFor="include-products" className="text-sm cursor-pointer">
                    Include products
                </Label>
            </div>
        </div>
    );
}

export default BulkApplyFilters;
